import { useMemo, useState } from "react";
import { Sparkles, CheckCircle2, CircleAlert, Minus, ChevronDown, ChevronUp, FileUp, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ContractStatusBadge } from "../ContractStatusBadge";
import { fmtDate } from "@/lib/contracts";
import { CORE_KEYS, FOCUSABLE_KEYS, KEY_ICONS, KEY_LABELS_FR, prio, valuesDiffer } from "./contractCreateShared";

import { translateFr as tfr } from "@/i18n";
const VISIBLE_COUNT = 7;

const StateChip = ({ state, tf }) => {
  const map = {
    confirmed: { icon: CheckCircle2, label: tfr("Confirmé"), cls: "text-emerald-500 bg-emerald-500/10 border-emerald-500/25" },
    review: { icon: CircleAlert, label: tfr("À vérifier"), cls: "text-amber-500 bg-amber-500/10 border-amber-500/25" },
    missing: { icon: Minus, label: tfr("Non détecté"), cls: "text-muted-foreground bg-foreground/[0.04] dark:bg-white/[0.04] border-border dark:border-white/[0.1]" },
    difference: { icon: CircleAlert, label: tfr("Différence"), cls: "text-red-500 bg-red-500/10 border-red-500/30" },
  };
  const m = map[state];
  const Icon = m.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border shrink-0", m.cls)}>
      <Icon className="h-3 w-3" /> {tf(m.label)}
    </span>
  );
};

function DetectedCard({ row, state, currentValue, tf, onFocus, index }) {
  const [showClause, setShowClause] = useState(false);
  const Icon = KEY_ICONS[row.key] || Sparkles;
  const label = KEY_LABELS_FR[row.key] ? tf(KEY_LABELS_FR[row.key]) : tf(row.label || row.key);
  const isLong = (row.value || "").length > 90;
  const summary = isLong ? `${row.value.slice(0, 80).trim()}…` : row.value;
  const clickable = !row.missing && FOCUSABLE_KEYS.includes(row.key) && onFocus;
  const Tag = clickable ? "button" : "div";

  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={clickable ? () => onFocus(row.key) : undefined}
      data-testid={`detected-${row.key}`}
      className={cn(
        "w-full flex items-start gap-3 rounded-xl border px-3 py-2.5 text-left bg-background/60 dark:bg-white/[0.02]",
        "border-border/70 dark:border-white/[0.07]",
        "animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none",
        clickable && "hover:border-red-500/35 hover:bg-foreground/[0.02] dark:hover:bg-white/[0.04] transition-colors motion-reduce:transition-none cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
      )}
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms`, animationFillMode: "both" }}
    >
      <div className="h-8 w-8 rounded-lg bg-foreground/[0.04] dark:bg-white/[0.05] border border-border dark:border-white/[0.08] flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[13px] font-medium leading-snug">{label}</p>
          <StateChip state={state} tf={tf} />
        </div>
        {row.missing ? (
          <p className="text-xs text-muted-foreground mt-0.5">{tf("Non détecté")}</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground mt-0.5 break-words">
              {row.source_page ? <span className="text-muted-foreground/70">p.{row.source_page} · </span> : null}
              {isLong && !showClause ? summary : row.value}
            </p>
            {state === "difference" && currentValue && (
              <p className="text-[11px] text-red-500/90 mt-0.5">{tf("Valeur saisie")} : {currentValue}</p>
            )}
            {isLong && (
              <button type="button" data-testid={`clause-toggle-${row.key}`}
                onClick={(e) => { e.stopPropagation(); setShowClause((v) => !v); }}
                className="text-[11px] text-red-500 hover:text-red-400 font-medium mt-1 inline-flex items-center gap-1">
                {showClause ? <><ChevronUp className="h-3 w-3" /> {tf("Masquer la clause")}</> : <><ChevronDown className="h-3 w-3" /> {tf("Voir la clause")}</>}
              </button>
            )}
          </>
        )}
      </div>
    </Tag>
  );
}

function DetectedPanel({ extraction, currentOf, tf, onFocusField, originNote }) {
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => {
    const fields = extraction.fields || [];
    const byKey = {};
    fields.forEach((f) => { if (!byKey[f.key]) byKey[f.key] = f; });
    const ordered = [...fields].sort((a, b) => prio(a.key) - prio(b.key));
    const missing = CORE_KEYS.filter((k) => k !== "title" && !byKey[k]).map((k) => ({ key: k, missing: true }));
    return [...ordered, ...missing];
  }, [extraction]);

  const stateOf = (row) => {
    if (row.missing) return "missing";
    if (CORE_KEYS.includes(row.key) && currentOf) {
      const cur = currentOf(row.key);
      if (cur && valuesDiffer(row.key, row.value, cur)) return "difference";
    }
    return row.confidence === "high" ? "confirmed" : "review";
  };

  const detected = rows.filter((r) => !r.missing);
  const confirmed = detected.filter((r) => r.confidence === "high").length;
  const review = detected.length - confirmed;
  const visible = expanded ? rows : rows.slice(0, VISIBLE_COUNT);
  const hidden = rows.length - VISIBLE_COUNT;

  return (
    <div className="rounded-2xl border border-red-500/20 dark:border-red-500/[0.18] bg-red-500/[0.02] dark:bg-red-500/[0.03] p-4 sm:p-5 space-y-3 min-w-0"
      data-testid="intelligence-panel">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-red-500/10 border border-red-500/25 flex items-center justify-center shrink-0">
          <Sparkles className="h-[18px] w-[18px] text-red-500" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold leading-snug">{tf("Informations détectées par")} Athlynx Intelligence</h3>
          {originNote && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{originNote}</p>}
        </div>
        <span data-testid="intelligence-badge"
          className={cn("inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border shrink-0",
            review === 0 ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/25" : "text-amber-500 bg-amber-500/10 border-amber-500/25")}>
          <CheckCircle2 className="h-3 w-3" />
          {review === 0 ? `${confirmed}/${detected.length} ${tf("confirmées")}` : `${confirmed} ${tf("confirmées")} · ${review} ${tf("à vérifier")}`}
        </span>
      </div>
      <div>
        <p className="text-xs text-muted-foreground" data-testid="import-summary">
          {tf("Athlynx Intelligence a extrait")} {detected.length} {tf("informations du document.")}
        </p>
        <p className="text-xs text-muted-foreground/80 mt-0.5">{tf("Vérifiez et corrigez si nécessaire avant de créer le contrat.")}</p>
      </div>
      <div className="space-y-2" data-testid="import-review">
        {visible.map((row, i) => (
          <DetectedCard key={row.key + String(i)} row={row} state={stateOf(row)} index={i}
            currentValue={!row.missing && CORE_KEYS.includes(row.key) && currentOf ? currentOf(row.key) : null}
            tf={tf} onFocus={onFocusField} />
        ))}
      </div>
      {hidden > 0 && (
        <button type="button" data-testid="detected-expand-btn" onClick={() => setExpanded((v) => !v)}
          className="w-full text-center text-xs font-medium text-red-500 hover:text-red-400 py-1.5 inline-flex items-center justify-center gap-1.5 transition-colors motion-reduce:transition-none">
          {expanded ? <><ChevronUp className="h-3.5 w-3.5" /> {tf("Réduire la liste")}</>
            : <><ChevronDown className="h-3.5 w-3.5" /> + {tf("Voir")} {hidden} {tf("autres informations détectées")}</>}
        </button>
      )}
    </div>
  );
}

function EmptyState({ tf, onImportRequest }) {
  return (
    <div className="rounded-2xl border border-dashed border-border dark:border-white/[0.12] p-6 flex flex-col items-center justify-center text-center gap-3 min-h-[240px]"
      data-testid="intelligence-empty-state">
      <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/25 flex items-center justify-center">
        <Sparkles className="h-5 w-5 text-red-500" />
      </div>
      <div>
        <p className="text-sm font-semibold">Athlynx Intelligence</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-[260px]">
          {tf("Importez un document pour pré-remplir automatiquement les informations du contrat.")}
        </p>
      </div>
      {onImportRequest && (
        <Button variant="outline" size="sm" onClick={onImportRequest} data-testid="intelligence-import-btn">
          <FileUp className="h-3.5 w-3.5 mr-1.5" /> {tf("Importer un document")}
        </Button>
      )}
    </div>
  );
}

function EditContextPanel({ contract, currentOf, tf }) {
  const missing = [
    [!currentOf("athlete_name"), tfr("Athlète")],
    [!currentOf("counterparty"), "Contrepartie"],
    [!currentOf("start_date") || !currentOf("end_date"), "Dates"],
    [!currentOf("value_amount"), "Montant"],
  ].filter(([m]) => m).map(([, l]) => tf(l));
  const lifecycleNote = ["pending_signature", "partially_signed", "signed", "upcoming", "active"].includes(contract?.status);

  return (
    <div className="rounded-2xl border border-border dark:border-white/[0.08] p-4 sm:p-5 space-y-4 min-w-0" data-testid="edit-context-panel">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-xl bg-foreground/[0.04] dark:bg-white/[0.05] border border-border dark:border-white/[0.08] flex items-center justify-center shrink-0">
          <Info className="h-[18px] w-[18px] text-muted-foreground" />
        </div>
        <h3 className="text-sm font-semibold flex-1">{tf("Complétude du dossier")}</h3>
        {contract && <ContractStatusBadge status={contract.status} />}
      </div>
      {missing.length ? (
        <p className="text-xs text-amber-500" data-testid="edit-completeness">{tf("À compléter")} : {missing.join(" · ")}</p>
      ) : (
        <p className="text-xs text-emerald-500 inline-flex items-center gap-1.5" data-testid="edit-completeness">
          <CheckCircle2 className="h-3.5 w-3.5" /> {tf("Dossier complet")}
        </p>
      )}
      {contract && (
        <div className="text-[11px] text-muted-foreground space-y-1">
          <p>{tf("Créé le")} {fmtDate(contract.created_at)}</p>
          <p>{tf("Dernière mise à jour")} : {fmtDate(contract.updated_at)}</p>
        </div>
      )}
      {lifecycleNote && (
        <p className="text-[11px] text-muted-foreground border border-border dark:border-white/[0.08] rounded-lg px-3 py-2 bg-foreground/[0.02] dark:bg-white/[0.02]">
          {tf("Les éléments signés et les procédures de signature restent immuables et gérés par le cycle de vie du contrat.")}
        </p>
      )}
    </div>
  );
}

export const IntelligencePanel = ({ mode, extraction, contract, currentOf, tf, onFocusField, onImportRequest }) => {
  if (extraction && (extraction.fields || []).length > 0) {
    const originNote = mode === "edit" && extraction.filename
      ? `${tf("Extraction d'origine")} · ${extraction.filename}` : null;
    return <DetectedPanel extraction={extraction} currentOf={currentOf} tf={tf} onFocusField={onFocusField} originNote={originNote} />;
  }
  if (mode === "edit") return <EditContextPanel contract={contract} currentOf={currentOf} tf={tf} />;
  return <EmptyState tf={tf} onImportRequest={onImportRequest} />;
};
